import React, { useEffect, useState } from 'react';
import { Col, Row, Select, Table } from 'antd';
import { api, endpoints } from '../../../../api';
import { TextbookCompletedSVG, TextbookDistributionSVG, TextbookExpertiseSVG } from '../../../../icons';
import {
  ExpertiseComponentContainer,
  CustomButton,
  CustomTableContainer,
  ExpandText,
  FormContainer,
  FormInput,
  SvgFormer,
  CustomSelect,
  GridItem,
  WarningNotification,
  ManualContainer,
} from '../../../../styles/styled';
import { sortPublishers, sortStatus } from '../../../../utils/sortObject';
import {
  ExpertiseSearchType,
  ExpertisesStatusType,
  PublishersType,
  SchoolClassesType,
} from '../../../NewApplicationPage/types';
import { ExpertiseTextbookResponse, ExpertiseTextbooksType, ExpertiseTextbookType, ExpertiseType } from '../../types';
import { ExpertsListComponent } from '../ExpertsListComponent';

export type PaginationType = {
  pagination: number;
  perPage: number;
  count: number;
};

const { Option } = Select;

export type TableExpertiseType = ExpertiseType & {
  key: number;
};

export const BooksExpertiseComponent = () => {
  const [paginationState, setPaginationState] = useState<PaginationType>({
    pagination: 1,
    perPage: 10,
    count: 0,
  });
  const [expertiseBooks, setExpertiseBooks] = useState<ExpertiseTextbooksType>([]);
  const [expandState, setExpandState] = useState<number[]>([]);
  const [publishersState, setPublishers] = useState<PublishersType>([]);
  const [schoolClassesState, setSchoolClasses] = useState<SchoolClassesType>([]);
  const [statusesState, setStatuses] = useState<ExpertisesStatusType>([]);
  const [searchState, setSearchState] = useState<ExpertiseSearchType>();
  const [loadSearch, setLoad] = useState<'search' | 'neutral' | 'empty'>('empty');
  const [loadTextbooks, setLoadTextbooks] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      try {
        const response = await api.get<SchoolClassesType>(endpoints.getSchoolClasses());
        const publishersResponse = await api.get<PublishersType>(endpoints.getPublishers(0, 1000));
        const textbookStatuses = await api.get<ExpertisesStatusType>(endpoints.getTextbookStatuses());

        setPublishers(publishersResponse.data.sort(sortPublishers));
        setStatuses(textbookStatuses.data.sort(sortStatus));
        setSchoolClasses(response.data);
      } catch (err) {}
    })();
  }, []);

  useEffect(() => {
    setExpandState([]);

    (async () => {
      const params = {
        page: paginationState.pagination - 1,
        perPage: paginationState.perPage,
        publisher: searchState?.publisher,
        author: searchState?.author,
        name: searchState?.title,
        year: searchState?.year,
        school_class: searchState?.school_class,
        status: searchState?.status,
      };

      if (loadSearch === 'empty') {
        const response = await api.get<ExpertiseTextbookResponse>(
          endpoints.getExpertiseTextbooks({ perPage: 10, page: paginationState.pagination - 1 }),
        );

        setExpertiseBooks(
          response.data.textbooks.map((el, index) => {
            return { ...el, key: index };
          }),
        );
      } else if (loadSearch === 'search') {
        const paginationResponse = await api.get<ExpertiseTextbookResponse>(
          endpoints.getExpertiseTextbooks({
            ...params,
            perPage: 10,
            page: paginationState.pagination - 1,
          }),
        );

        setPaginationState({ ...paginationState, count: paginationResponse.data.count });
        if (paginationResponse.data)
          setExpertiseBooks(
            paginationResponse.data.textbooks.map((el, index) => {
              return { ...el, key: index };
            }),
          );

        setLoad('neutral');

        return;
      }
    })();

    // eslint-disable-next-line
  }, [loadSearch, loadTextbooks, paginationState.pagination]);

  useEffect(() => {
    setLoad(searchState ? 'search' : 'empty');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadTextbooks]);

  useEffect(() => {
    if (loadSearch !== 'empty') {
      return;
    }

    setExpandState([]);

    (async () => {
      const response = await api.get<ExpertiseTextbookResponse>(
        endpoints.getExpertises({
          page: paginationState.pagination - 1,
          perPage: paginationState.perPage,
        }),
      );

      setPaginationState({
        ...paginationState,
        count: response.data.count,
      });

      if (response?.data?.textbooks)
        setExpertiseBooks(
          response.data.textbooks.map((el, index) => {
            return { ...el, key: index };
          }),
        );
    })();
    // eslint-disable-next-line
  }, [paginationState.pagination, loadTextbooks, loadSearch]);

  const expandRow = (id: number) => {
    setExpandState((prevState) => {
      return prevState.includes(id) ? prevState : [...prevState, id];
    });
  };

  const closeRow = (id: number) => {
    setExpandState((prevState) => {
      return prevState.filter((el) => el !== id);
    });
  };

  const expandAll = () => {
    setExpandState(expertiseBooks.map((el) => el.key));
  };

  const closeAll = () => {
    setExpandState([]);
  };

  const searchButtonHandler = () => {
    setLoad('search');
    setPaginationState({ ...paginationState, pagination: 1 });
  };

  const expertiseTableColumns = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'id',
      render: (value: string, book: ExpertiseTextbookType) => (
        <p>
          {book.key !== undefined
            ? (paginationState.pagination === 1 ? 0 : paginationState.pagination - 1) * paginationState.perPage +
              (paginationState.pagination === 1 ? book.key + 1 : book.key)
            : 1}
        </p>
      ),
    },
    {
      title: 'Наименование учебника',
      dataIndex: 'title',
      key: 'title',
      render: (element: string, book: ExpertiseTextbookType) => <p> {book.title} </p>,
    },
    {
      title: 'Класс',
      dataIndex: 'school_classes',
      key: 'school_classes',
      render: (element: string, book: ExpertiseTextbookType) => (
        <p> {book.school_classes.map((el) => el.title).join(', ')} </p>
      ),
    },
    {
      title: 'Автор (авторский коллектив)',
      dataIndex: 'author',
      key: 'author',
      render: (element: string, book: ExpertiseTextbookType) => <ManualContainer>{book.author}</ManualContainer>,
    },
    {
      title: 'Наименование учебного пособия',
      dataIndex: 'manual_title',
      key: 'manual_title',
      render: (value: string, book: ExpertiseTextbookType) => {
        return book.text_book_edu_man?.map(
          (el, idx, arr) =>
            el.title && (
              <ManualContainer key={`${book.key}-${idx}`}>
                {el.title}
                {idx < arr.length - 1 && ';'}
              </ManualContainer>
            ),
        );
      },
    },
    {
      title: 'Автор (авторский коллектив) учебного пособия',
      dataIndex: 'manual_author',
      key: 'manual_author',
      render: (value: string, book: ExpertiseTextbookType) =>
        book?.text_book_edu_man?.map(
          (el, idx, arr) =>
            el.fio && (
              <ManualContainer key={`${book.key}-${idx}`}>
                {el.fio}
                {idx < arr.length - 1 && ';'}
              </ManualContainer>
            ),
        ),
    },
    {
      title: 'Наименование издательства',
      dataIndex: 'publisher',
      key: 'publisher',
      render: (element: string, book: ExpertiseTextbookType) => (
        <p> {book.line_of_textbooks.application.publisher.title} </p>
      ),
    },
    {
      title: 'Дата регистрации заявления',
      dataIndex: 'date',
      key: 'date',
      render: (value: string, expertise: ExpertiseTextbookType) => (
        <p>{expertise.line_of_textbooks.application.date?.split('-')?.reverse()?.join('.')}</p>
      ),
    },
    {
      title: 'Статус экспертизы',
      dataIndex: 'status',
      key: 'status',
      render: (value: string, book: ExpertiseTextbookType) => (
        <SvgFormer>
          {book.status?.code === 'distribution' ? (
            <TextbookDistributionSVG />
          ) : book.status?.code === 'expertise' ? (
            <TextbookExpertiseSVG />
          ) : book.status?.code === 'completed' ? (
            <TextbookCompletedSVG />
          ) : (
            book.status?.code
          )}
        </SvgFormer>
      ),
    },
    {
      title: (
        <GridItem align="center" justify="center" direction="row" wrap="nowrap" gap="5px">
          <button
            className="ant-table-cell underlined"
            onClick={() => (expertiseBooks.every((e) => expandState.includes(e.key)) ? closeAll() : expandAll())}
          >
            Состав экспертов
          </button>
          {expertiseBooks.every((e) => expandState.includes(e.key)) ? (
            <span className="arrow rotate">{'>'}</span>
          ) : (
            <span className="arrow">{'>'}</span>
          )}
        </GridItem>
      ),
      dataIndex: 'expand',
      key: 'expand',
      render: (record: string, book: ExpertiseTextbookType) => {
        const key = book.key;

        return key > -1 ? (
          <ExpandText onClick={() => (expandState.includes(key) ? closeRow(key) : expandRow(key))}>
            <GridItem gap="5px" noText align="center" justify="center" direction="row">
              <div className="text">ЭКСПЕРТЫ</div>
              {expandState.includes(key) ? <div className="rotate">{'>'}</div> : '>'}
            </GridItem>
            {book.status.code === 'distribution' && book.expertises.length === 0 && (
              <WarningNotification>
                <div className="notify-item">! </div>Добавить эксперта
              </WarningNotification>
            )}
          </ExpandText>
        ) : (
          <p>-</p>
        );
      },
    },
  ];

  return (
    <ExpertiseComponentContainer>
      <FormContainer padding="10px" background={'#e9eef4'}>
        <Row gutter={[15, 15]}>
          <Col span={12}>
            <div className="text">Наименование учебника</div>
            <FormInput
              value={searchState?.title || ''}
              onChange={(e) =>
                setSearchState((prevState) => {
                  return { ...prevState, title: e.target.value as string };
                })
              }
            />
          </Col>
          <Col span={5}>
            <div className="text">Класс</div>
            <CustomSelect
              onChange={(value, option) => {
                setSearchState({
                  ...searchState,
                  school_class: parseInt(Object.values(option)[0]),
                });
              }}
              value={
                searchState?.school_class
                  ? schoolClassesState.find((el) => parseInt(el.id) === searchState.school_class)?.title
                  : 'Все'
              }
              showSearch
            >
              <Option key={undefined} value={'Все'}>
                Все
              </Option>
              {schoolClassesState.map((el) => {
                return (
                  <Option key={el.id} value={el.title || ''}>
                    {el.title}
                  </Option>
                );
              })}
            </CustomSelect>
          </Col>
          <Col span={5} />
          <Col span={12}>
            <div className="text">Автор (авторский коллектив)</div>
            <FormInput
              value={searchState?.author || ''}
              onChange={(e) =>
                setSearchState((prevState) => {
                  return { ...prevState, author: e.target.value };
                })
              }
            />
          </Col>
          <Col span={12}>
            <div className="text">Наименование издательства</div>

            <CustomSelect
              onChange={(value, option) => {
                setSearchState({
                  ...searchState,
                  publisher: value ? parseInt(Object.values(option)[0]) : undefined,
                });
              }}
              value={
                searchState?.publisher ? publishersState.find((el) => el.id === searchState.publisher)?.title : 'Все'
              }
              showSearch
            >
              <Option key={undefined} value={undefined}>
                Все
              </Option>
              {publishersState.map((el) => {
                return (
                  <Option key={el.id} value={el.title}>
                    {el.title}
                  </Option>
                );
              })}
            </CustomSelect>
          </Col>
          <Col span={12}>
            <div className="text">Статус экспертизы учебника</div>
            <CustomSelect
              onChange={(value, option) => {
                setSearchState({
                  ...searchState,
                  status: parseInt(Object.values(option)[0]),
                });
              }}
              value={searchState?.status ? statusesState.find((el) => el.id === searchState.status)?.name : 'Все'}
              showSearch
            >
              {statusesState.map((el) => {
                return (
                  <Option key={el.id} value={el.name}>
                    {el.name}
                  </Option>
                );
              })}
            </CustomSelect>
          </Col>
          <Col span={12}>
            <div className="text red-star">Год поступления материала</div>
            <CustomSelect
              value={searchState?.year ? searchState.year : 'Все'}
              onChange={(value, option) => {
                setSearchState((prevState) => {
                  return { ...prevState, year: Object.values(option)[1] };
                });
              }}
              showSearch
            >
              <Option value="" key={0}>
                Все
              </Option>
              <Option value="2020" key={2020}>
                2020
              </Option>
              <Option value="2021" key={2021}>
                2021
              </Option>
              <Option value="2022" key={2022}>
                2022
              </Option>
              <Option value="2023" key={2023}>
                2023
              </Option>
              <Option key="2024" value={2024}>
                2024
              </Option>
              <Option key="2025"  value={2025}>
                2025
              </Option>
            </CustomSelect>
          </Col>
          <Col className="center padding-top-20px" span={24}>
            <CustomButton width="100%" onClick={searchButtonHandler}>
              Найти
            </CustomButton>
          </Col>
        </Row>
      </FormContainer>

      <FormContainer>
        <div className="right">
          <CustomButton
            onClick={() => {
              setLoad('empty');
              setSearchState({});
            }}
            lined
          >
            Очистить
          </CustomButton>
        </div>
      </FormContainer>
      <CustomTableContainer>
        <Table
          columns={expertiseTableColumns}
          dataSource={expertiseBooks}
          expandable={{
            expandedRowRender: (record: ExpertiseTextbookType) => {
              return (
                <ExpertsListComponent
                  textbookId={record.id}
                  textbookStatus={record?.status}
                  textbook={record}
                  experts={record.expertises}
                  setLoad={() => setLoadTextbooks(!loadTextbooks)}
                />
              );
            },
            expandedRowKeys: expandState,
            expandIconColumnIndex: -1,
          }}
          pagination={{
            current: paginationState.pagination,
            pageSize: paginationState.perPage,
            total: paginationState.count,
            onChange: (value, pageSize) => {
              setLoad('search');
              setPaginationState((prevState) => {
                return { ...prevState, pagination: value, perPage: pageSize };
              });
            },
          }}
        />
      </CustomTableContainer>
    </ExpertiseComponentContainer>
  );
};
